import { useState } from 'react';
import { MessageCircle, MessageSquareText, MessagesSquare, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import useCustomerIO from '@/hooks/useCustomerIO';
import type { UpdateCall, UpdateComment } from '@/types/orderUpdates';
import type { UpdatesSection } from '@/hooks/useOrderUpdates';
import type { UpdatesTimeline } from './useUpdatesTimeline';
import { useUpdatesOrderUid } from './updatesContext';
import { WhatsAppChat } from './WhatsAppChat';
import { collapseCallRuns } from './timeline';
import { formatCallDuration, formatUpdateClock, localDigits, pickLang } from './updatesFormat';

/** The design draws two calls. QA's real order had nineteen, most of them identical attempts, so
 *  back-to-back repeats collapse to one row and the list opens at the newest ten. */
const SECTION_PAGE = 10;

const CARD = 'rounded-2xl border border-solid border-sd-border bg-sd-card';

function SectionHead({
  icon,
  title,
  meta,
}: {
  icon: ReactNode;
  title: string;
  meta?: string | null;
}) {
  return (
    <div className='flex items-center gap-2 px-1'>
      <span aria-hidden>{icon}</span>
      <h3 className='m-0 text-[14px] font-bold text-sd-foreground'>{title}</h3>
      {meta ? (
        <>
          <span className='flex-1' />
          <span className='text-[12px] tabular-nums text-sd-muted-foreground'>{meta}</span>
        </>
      ) : null}
    </div>
  );
}

function ShowEarlier({ count, onClick }: { count: number; onClick: () => void }) {
  const { t, i18n } = useTranslation();
  return (
    <li role='presentation' className='border-0 border-t border-solid border-sd-border px-4 py-2.5'>
      <button
        type='button'
        onClick={onClick}
        className='cursor-pointer rounded border-0 bg-transparent p-0 text-[13px] font-semibold text-sd-link underline-offset-2 hover:underline'
      >
        {localDigits(String(t('updates_show_earlier', { count })), i18n.language || 'en')}
      </button>
    </li>
  );
}

function Empty({ text }: { text: string }) {
  return <p className='m-0 px-4 py-6 text-center text-[13px] text-sd-muted-foreground'>{text}</p>;
}

function Unavailable({ section }: { section: UpdatesSection }) {
  const { t } = useTranslation();
  const key =
    section === 'calls'
      ? 'updates_calls_unavailable'
      : section === 'comments'
        ? 'updates_comments_unavailable'
        : 'updates_whatsapp_unavailable';
  return (
    <p
      role='alert'
      className='m-0 rounded-lg bg-[hsla(35,85%,52%,0.10)] px-3 py-2 text-[13px] text-[hsl(35,75%,32%)]'
    >
      {String(t(key))}
    </p>
  );
}

/** The transcript, in a dialog rather than inline: at 20 calls the page became unreadable. */
function TranscriptDialog({ call, onClose }: { call: UpdateCall | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const text = typeof call?.transcript?.text === 'string' ? call.transcript.text.trim() : '';
  const dir =
    call?.transcript?.text_dir === 'rtl' || call?.transcript?.text_dir === 'ltr'
      ? call.transcript.text_dir
      : 'auto';
  const seconds = call?.duration_seconds;
  const meta = call
    ? [
        String(t('updates_outgoing')),
        typeof seconds === 'number' && seconds > 0 ? formatCallDuration(seconds, lang) : null,
        formatUpdateClock(call.at, lang),
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <Dialog open={Boolean(call)} onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle>{String(t('updates_transcript_title'))}</DialogTitle>
          <DialogDescription className='tabular-nums'>{localDigits(meta, lang)}</DialogDescription>
        </DialogHeader>
        <div className='max-h-[60vh] overflow-y-auto'>
          <p
            dir={dir}
            data-testid='call-transcript'
            className='m-0 whitespace-pre-line text-start text-[15px] leading-relaxed'
          >
            {text}
          </p>
        </div>
        {/* Said once, where a merchant asks it: he gets the words, never the audio. */}
        <p className='m-0 border-0 border-t border-solid border-sd-border pt-3 text-[12px] text-sd-muted-foreground'>
          {String(t('updates_no_recording'))}
        </p>
      </DialogContent>
    </Dialog>
  );
}

function CallRow({
  call,
  attempts = 1,
  onTranscript,
}: {
  call: UpdateCall;
  attempts?: number;
  onTranscript: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const answered = call.outcome === 'answered';
  const label = pickLang(call.label, lang);
  const seconds = call.duration_seconds;
  const who =
    call.actor === 'system' ? String(t('updates_role_system')) : String(t('updates_role_support'));
  const hasTranscript = typeof call.transcript?.text === 'string' && call.transcript.text.trim();
  const outcome =
    label || String(t(answered ? 'updates_call_status_answered' : 'updates_call_status_no_answer'));

  return (
    <li className='flex flex-col gap-2 border-0 border-t border-solid border-sd-border px-4 py-3 first:border-t-0'>
      <div className='flex items-center gap-2 text-[13px]'>
        <span className='font-semibold text-sd-foreground'>{who}</span>
        <span className='text-sd-muted-foreground'>{String(t('updates_outgoing'))}</span>
        <span className='flex-1' />
        <span className='tabular-nums text-[12px] text-sd-muted-foreground'>
          {localDigits(formatUpdateClock(call.at, lang), lang)}
        </span>
      </div>

      <div className='flex flex-wrap items-center gap-2'>
        <span
          title={outcome}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-semibold ${
            answered ? 'bg-[#DFF1E7] text-[#226B4B]' : 'bg-[#FBE3E3] text-[#B83232]'
          }`}
        >
          {outcome}
          {attempts > 1 ? (
            <span className='tabular-nums'>{localDigits(`×${attempts}`, lang)}</span>
          ) : null}
        </span>
        {answered && typeof seconds === 'number' && seconds > 0 ? (
          <span className='text-[13px] tabular-nums text-sd-muted-foreground'>
            {localDigits(formatCallDuration(seconds, lang), lang)}
          </span>
        ) : null}
      </div>

      {hasTranscript ? (
        <button
          type='button'
          onClick={onTranscript}
          className='inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-xl border border-solid border-sd-border bg-transparent px-3 text-[13px] font-semibold text-sd-foreground hover:bg-sd-muted/50'
        >
          <MessagesSquare className='h-4 w-4' strokeWidth={2} aria-hidden />
          {String(t('updates_view_transcript'))}
        </button>
      ) : null}
    </li>
  );
}

const COMMENT_ROLE: Record<string, string> = {
  support: 'updates_role_support',
  last_mile: 'updates_role_last_mile',
  system: 'updates_role_system',
};

function CommentRow({ comment }: { comment: UpdateComment }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const role = comment.role ? (COMMENT_ROLE[comment.role] ?? '') : '';
  const dir = comment.text_dir === 'rtl' || comment.text_dir === 'ltr' ? comment.text_dir : 'auto';
  return (
    <li className='flex flex-col gap-1 border-0 border-t border-solid border-sd-border px-4 py-3 first:border-t-0'>
      <div className='flex items-center gap-2'>
        <span className='rounded-full bg-[#F7E9DD] px-2 py-0.5 text-[12px] font-semibold text-[#8F4F1E]'>
          {role ? String(t(role)) : comment.role}
        </span>
        <span className='flex-1' />
        <span className='tabular-nums text-[12px] text-sd-muted-foreground'>
          {localDigits(formatUpdateClock(comment.at, lang), lang)}
        </span>
      </div>
      <p dir={dir} className='m-0 text-start text-[15px] leading-relaxed'>
        {comment.text}
      </p>
    </li>
  );
}

/**
 * BOXY-715 — comments, calls and WhatsApp as three named sections instead of one merged stream.
 *
 * The merged timeline made a merchant read every row to answer "what did the customer say on
 * WhatsApp?". Each source now has its own card, and the filter chips choose which to show.
 */
export function UpdatesSections({ timeline }: { timeline: UpdatesTimeline }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const { trackEvent } = useCustomerIO();
  const orderUid = useUpdatesOrderUid();
  const [transcript, setTranscript] = useState<UpdateCall | null>(null);
  const [allCalls, setAllCalls] = useState(false);
  const [allComments, setAllComments] = useState(false);
  const { filter, counts, down, whatsapp, calls, comments, answered } = timeline;
  const callRuns = collapseCallRuns(
    calls.map((call, i) => ({ kind: 'calls' as const, key: `c-${call.id}-${i}`, at: String(call.at), call, run: [call] })),
  );
  const shownCalls = allCalls ? callRuns : callRuns.slice(0, SECTION_PAGE);
  const shownComments = allComments ? comments : comments.slice(0, SECTION_PAGE);
  const shows = (section: UpdatesSection) => filter === 'all' || filter === section;

  const openTranscript = (call: UpdateCall) => {
    trackEvent('call_transcript_expanded', { order_uid: orderUid });
    setTranscript(call);
  };

  const nothingAtAll =
    filter === 'all' && !counts.calls && !counts.comments && !counts.whatsapp && down.length === 0;

  if (nothingAtAll) {
    return (
      <div className={`${CARD} flex flex-col items-center gap-2 px-4 py-10 text-center`}>
        <p className='m-0 text-[14px] font-semibold text-sd-foreground'>
          {String(t('updates_empty_title'))}
        </p>
        <p className='m-0 max-w-sm text-[13px] text-sd-muted-foreground'>
          {String(t('updates_empty_hint'))}
        </p>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      {shows('comments') ? (
        <section className='flex flex-col gap-2'>
          <SectionHead
            icon={<MessageSquareText className='h-4 w-4 text-[#B5692E]' strokeWidth={2} />}
            title={String(t('updates_section_comments'))}
          />
          {down.includes('comments') ? (
            <Unavailable section='comments' />
          ) : (
            <ul className={`m-0 list-none p-0 ${CARD}`}>
              {comments.length ? (
                shownComments.map((comment, i) => (
                  <CommentRow key={`${comment.id}-${i}`} comment={comment} />
                ))
              ) : (
                <Empty text={String(t('updates_no_comments_yet'))} />
              )}
              {!allComments && comments.length > SECTION_PAGE ? (
                <ShowEarlier
                  count={comments.length - SECTION_PAGE}
                  onClick={() => setAllComments(true)}
                />
              ) : null}
            </ul>
          )}
        </section>
      ) : null}

      {shows('calls') ? (
        <section className='flex flex-col gap-2'>
          <SectionHead
            icon={<Phone className='h-4 w-4 text-[#2E7D5B]' strokeWidth={2} />}
            title={String(t('updates_section_calls'))}
            meta={
              counts.calls
                ? localDigits(
                    String(t('updates_calls_meta', { count: counts.calls, answered })),
                    lang,
                  )
                : null
            }
          />
          {down.includes('calls') ? (
            <Unavailable section='calls' />
          ) : (
            <ul className={`m-0 list-none p-0 ${CARD}`}>
              {calls.length ? (
                shownCalls.map((item) =>
                  item.kind === 'calls' ? (
                    <CallRow
                      key={item.key}
                      call={item.call}
                      attempts={item.run.length}
                      onTranscript={() => openTranscript(item.call)}
                    />
                  ) : null,
                )
              ) : (
                <Empty text={String(t('updates_no_calls_yet'))} />
              )}
              {!allCalls && callRuns.length > SECTION_PAGE ? (
                <ShowEarlier
                  count={callRuns.length - SECTION_PAGE}
                  onClick={() => setAllCalls(true)}
                />
              ) : null}
              {/* Only once the merchant is looking at the whole list: before that it is not true. */}
              {timeline.truncated && (allCalls || callRuns.length <= SECTION_PAGE) ? (
                <li role='presentation' className='border-0 border-t border-solid border-sd-border px-4 py-2.5 text-[12px] text-sd-muted-foreground'>
                  {String(t('updates_calls_truncated'))}
                </li>
              ) : null}
            </ul>
          )}
        </section>
      ) : null}

      {shows('whatsapp') ? (
        <section className='flex flex-col gap-2'>
          <SectionHead
            icon={<MessageCircle className='h-4 w-4 text-[#2E7D5B]' strokeWidth={2} />}
            title={String(t('updates_section_whatsapp'))}
          />
          {down.includes('whatsapp') ? (
            <Unavailable section='whatsapp' />
          ) : whatsapp.length ? (
            <WhatsAppChat messages={whatsapp} />
          ) : (
            <div className={CARD}>
              <Empty text={String(t('updates_no_whatsapp_yet'))} />
            </div>
          )}
        </section>
      ) : null}

      <TranscriptDialog call={transcript} onClose={() => setTranscript(null)} />
    </div>
  );
}
